/* eslint-disable react/no-danger */
/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import useLocationNext from '../../hooks/useLocationNext';
// import usePage from '../../hooks/usePage';
import { getComponentFromName } from '../../lib/utils';

import { useContest } from '../../contexts/ContestContext';
import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import LoginForm from '../forms/LoginForm';
import HelpModal from '../modals/HelpModal';
// import * as LogosComponents from '../icons/logos/index';
import AgeGate from '../partials/AgeGate';
import Popup from '../partials/Popup';
import * as SplashComponents from '../splashes/index';

import styles from '../../../styles/pages/home-default.module.scss';

interface HomePageProps {
    page: HomePage | null;
    className?: string;
    splashClassName?: string;
}

function HomePage({ page, className, splashClassName }: HomePageProps) {
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const next = useLocationNext();

    const {
        optins,
        hasPincodes = false,
        theme,
        ended = false,
        started = false,
        access,
        title,
        startAt,
    } = useContest() || {};

    const {
        ageGate = false,
        ageGateComponent = null,
        profile = null,
        requiresPincode: hasPincodesField = false,
        disabledProvinces = null,
    } = access || {};

    const { redirectOnEnter: redirectToProfile = false } = profile || {};

    const { logo } = theme || {};
    const themeClassName = useThemeClassName(styles);
    const { component: logoComponent } = logo || {};
    const { splash = null, blocks, notStartedMessage = null, loginForm, popup = null } = page || {};
    const { endedMessage = null } = useLabels() || {};

    const { component: splashComponent = null } = splash || {};
    const { title: loginFormTitle = null, optinsAfterFields = false } = loginForm || {};
    const SplashComponent = getComponentFromName(SplashComponents, splashComponent, logoComponent);

    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);

    const [showPopup, setShowPopup] = useState(false);
    const [province, setProvince] = useState(Cookies.get('ag-province') || null);
    const [birthdate, setBirthdate] = useState(Cookies.get('ag-birthdate') || null);
    const [unlocked, setUnlocked] = useState(!ageGate);
    const [registering, setRegistering] = useState(false);
    const [userProfileCompleted, setUserProfileCompleted] = useState(true);

    const onProvinceChange = useCallback((value) => {
        setProvince(value);
    }, []);

    const onBirthdateChange = useCallback((value) => {
        setBirthdate(value);
    }, []);

    const redirect = useCallback(() => {
        if (!userProfileCompleted || (registering && redirectToProfile)) {
            navigate(route('register'));
        } else if (next !== null && next.length > 0) {
            navigate(next);
        } else {
            navigate(ended ? route('account') : route('play'));
        }
    }, [navigate, route, next, ended, userProfileCompleted, registering]);

    const onLoginSucess = useCallback(
        (user, isRegister) => {
            const {
                unusedPincode = null,
                profileCompleted = true,
                lastPincode = null,
            } = user || {};

            setUserProfileCompleted(profileCompleted);
            setRegistering(isRegister);

            if (popup !== null) {
                const { groups: popupGroups = null } = popup || {};
                const { groups = null } = lastPincode || {};

                // If popup groups are set
                if (popupGroups !== null) {
                    if (
                        groups !== null &&
                        (popupGroups || []).filter((group: string) => {
                            return groups.indexOf(group) !== -1;
                        }).length > 0
                    ) {
                        setShowPopup(true);
                        return;
                    }
                } else {
                    setShowPopup(true);
                    return;
                }
            }

            redirect();
        },
        [
            navigate,
            route,
            next,
            ended,
            profile,
            setShowPopup,
            setUserProfileCompleted,
            setRegistering,
            redirect,
        ],
    );

    const onClosePopup = useCallback(() => {
        setShowPopup(false);
        redirect();
    }, [redirect, setShowPopup]);

    const onAgeGateSuccess = useCallback(({ provinceId, birthdate: date }) => {
        Cookies.set('ag-province', provinceId, { expires: 3600 });
        Cookies.set('ag-birthdate', date, { expires: 3600 });
        setUnlocked(true);
    }, []);

    useEffect(() => {
        if (unlocked) {
            window.scroll({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [unlocked]);

    const loginExtraFields = useMemo(
        () => ({ region: province, birthdate }),
        [province, birthdate],
    );

    const notStarted = !started && !ended;

    const pincodeModalBlocks = blocks.filter(({ placement }) => placement === 'pincode_modal');
    const normalBlocks = blocks.filter(({ placement = null }) => placement === null);

    const [pincodeModalOpened, setPincodeModalOpened] = useState(false);
    const onClickPincodeHelp = useCallback(() => {
        setPincodeModalOpened(true);
    }, []);
    const onPopupModalClose = useCallback(() => {
        setPincodeModalOpened(false);
    }, []);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.unlocked]: unlocked,
                    [styles.withPincode]: hasPincodes,
                    [styles.centered]: notStarted,
                    [styles.ended]: ended,
                },
                themeClassName,
                className,
            ])}
        >
            {showPopup && popup !== null ? <Popup popup={popup} onClose={onClosePopup} /> : null}

            {SplashComponent !== null ? (
                <div className={classNames([styles.splashContainer, splashClassName])}>
                    <SplashComponent unlocked={unlocked} {...splash} className={styles.splash} />
                </div>
            ) : null}

            {notStarted ? (
                <div className={styles.inner}>
                    {notStartedMessage !== null ? (
                        <div
                            className={styles.emphasis}
                            dangerouslySetInnerHTML={{ __html: notStartedMessage }}
                        />
                    ) : (
                        <div className={styles.emphasis}>
                            <FormattedMessage
                                defaultMessage="Get ready!{br}{br}{contest} starts{br}{date}."
                                description="Not started contest message"
                                values={{
                                    contest: title,
                                    date: (
                                        <FormattedDate
                                            value={startAtDate}
                                            day="numeric"
                                            month="long"
                                            year="numeric"
                                        />
                                    ),
                                }}
                            />
                        </div>
                    )}
                </div>
            ) : null}
            {ended ? (
                <div className={styles.inner}>
                    {endedMessage !== null ? (
                        <div
                            className={styles.emphasis}
                            dangerouslySetInnerHTML={{ __html: endedMessage }}
                        />
                    ) : (
                        <div className={styles.emphasis}>
                            <FormattedMessage
                                defaultMessage="{contest} has ended, come back next time!"
                                description="Ended contest message"
                                values={{
                                    contest: title,
                                }}
                            />
                        </div>
                    )}
                </div>
            ) : null}
            {started && !ended ? (
                <div className={styles.inner}>
                    {ageGate !== null && ageGate === true ? (
                        <div className={styles.ageGateContainer}>
                            <AgeGate
                                componentName={ageGateComponent}
                                className={styles.ageGate}
                                inputClassName={styles.loginInput}
                                inputDateClassName={styles.loginDateInput}
                                legalClassName={styles.ageGateLegal}
                                provinceId={province}
                                disabledProvinces={disabledProvinces}
                                birthdate={birthdate}
                                onSuccess={onAgeGateSuccess}
                                onProvinceChange={onProvinceChange}
                                onBirthdateChange={onBirthdateChange}
                                setUnlocked={setUnlocked}
                            />
                        </div>
                    ) : null}
                    <div className={styles.lockedContent}>
                        <LoginForm
                            className={styles.login}
                            labelClassName={styles.loginFormLabel}
                            phoneLabelClassName={styles.phoneLabel}
                            pincodeLabelClassName={styles.pincodeLabel}
                            inputClassName={styles.loginInput}
                            submitClassName={styles.loginSubmit}
                            errorsClassName={styles.loginErrors}
                            title={loginFormTitle}
                            withPincode={hasPincodesField && hasPincodes}
                            withPincodeHelp={pincodeModalBlocks.length > 0}
                            optins={optins}
                            optinsAfterFields={optinsAfterFields}
                            extraFields={loginExtraFields}
                            onSuccess={onLoginSucess}
                            onClickPincodeHelp={onClickPincodeHelp}
                        />
                        <div className={classNames(styles.pageContent, 'page-blocks')}>
                            <Blocks
                                blockClassName={styles.block}
                                blockTypeClassNames={styles}
                                blocks={normalBlocks}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            {pincodeModalOpened ? (
                <HelpModal onClose={onPopupModalClose}>
                    <Blocks blockClassName={styles.block} blocks={pincodeModalBlocks} />
                </HelpModal>
            ) : null}
        </div>
    );
}

export default HomePage;
